import { PaymentRequest } from '@stripe/stripe-js'
import { v4 as uuid } from 'uuid'

import init from '@/utils/initScript'
import IconAmex from '@/components/icons/payment/IconAmex'
import IconDiners from '@/components/icons/payment/IconDiners'
import IconDiscover from '@/components/icons/payment/IconDiscover'
import IconJCB from '@/components/icons/payment/IconJCB'
import IconMastercard from '@/components/icons/payment/IconMastercard'
import IconUnionpay from '@/components/icons/payment/IconUnionpay'
import IconVisa from '@/components/icons/payment/IconVisa'

export const paymentResultsPath = '/api/fesc/v2/callback/payment'

export const paymentTypes = {
  offline: 0,
  paypal: 2,
  taishin: 4,
  chinatrust: 5,
  braintree: 6,
  stripe: 7,
  omise: 8,
  paypalExpressCheckout: 10,
  veritrans: 11,
  ipay88: 12,
  paydollar: 13,
  paylah: 14,
  wirecard: 15,
  boost: 16,
  linePay: 17,
  oddleVoucher: 19,
  stripeExpressAccount: 20,
}

export const loadStripeScript = (): Promise<void> =>
  init('https://js.stripe.com/v3/', 'stripe-js')

export const extendPaymentMethodsWithStripe = async ({
  paymentMethods,
  storeOrder,
  country,
  currency,
}: {
  paymentMethods: Oms.PaymentMethod[]
  storeOrder: Oms.StoreOrder
  country: string
  currency: string
}): Promise<{
  extendedPaymentMethods: Oms.PaymentMethod[]
  stripePaymentRequestObject: PaymentRequest | undefined
}> => {
  const extendedPaymentMethods = paymentMethods
  let stripePaymentRequestObject: PaymentRequest | undefined
  const stripePaymentMethod = paymentMethods?.find(
    (paymentMethod) =>
      paymentMethod.type === paymentTypes.stripe ||
      paymentMethod.type === paymentTypes.stripeExpressAccount
  )

  // Check if can extend with Stripe Payment Request payment.
  if (
    (stripePaymentMethod?.paymentProcessToken?.length || 0) > 0 &&
    stripePaymentMethod?.isStripePaymentRequestButtonEnabled === true
  ) {
    await loadStripeScript()
    const stripeObject = window.Stripe?.(
      stripePaymentMethod.paymentProcessToken
    )
    stripePaymentRequestObject = stripeObject?.paymentRequest?.({
      country: country,
      currency: currency,
      total: {
        amount: storeOrder.totalAmountInSubUnit,
        label: storeOrder.menuName,
      },
    })
    // YX said we need reimplement apple payment later
    // const canMakePayment = await stripePaymentRequestObject.canMakePayment()
    // if (canMakePayment) {
    //   // Get all stripe payment methods available.
    //   const stripePaymentMethods = []
    //   if (canMakePayment.applePay === true) {
    //     // Handle only Apple Pay for now.
    //     const applePayMethod = {
    //       ...stripePaymentMethod,
    //       paymentName: 'Apple Pay',
    //       stripeRequestPaymentButtonType: paymentMethods.APPLE_PAY,
    //       stripeRequestPayment: true
    //     }
    //     stripePaymentMethods.push(applePayMethod)
    //     extendedPaymentMethods = [
    //       ...stripePaymentMethods,
    //       ...extendedPaymentMethods
    //     ]
    //   }
    // }
  }

  return {
    extendedPaymentMethods,
    stripePaymentRequestObject,
  }
}

export const extendPaymentMethodsWithParsedIdField = (
  paymentMethods: Oms.PaymentMethod[]
): Oms.PaymentMethod[] => {
  // Add parseId field to support render selected method.
  return paymentMethods.map((method) => {
    return { ...method, parsedId: uuid() }
  })
}

type CreditCardBrand =
  | 'amex'
  | 'diners'
  | 'discover'
  | 'jcb'
  | 'mastercard'
  | 'unionpay'
  | 'visa'

const iconCreditCard: Record<CreditCardBrand, any> = {
  amex: IconAmex,
  diners: IconDiners,
  discover: IconDiscover,
  jcb: IconJCB,
  mastercard: IconMastercard,
  unionpay: IconUnionpay,
  visa: IconVisa,
}

const brandCreditCard: Record<CreditCardBrand, string> = {
  amex: 'Amex',
  diners: 'Diners',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'MasterCard',
  unionpay: 'UnionPay',
  visa: 'Visa',
}

export const getCreditCardInfo = (brand: string) => ({
  icon: iconCreditCard[brand as CreditCardBrand],
  title: brandCreditCard[brand as CreditCardBrand],
})
